/**
 * Which fields a person has touched since a form was loaded.
 *
 * Every form that saves only what changed needs this, and the answer cannot be
 * read from the values alone: a validated form turns a blank optional field into
 * undefined, so a field that was cleared and a field that never held anything
 * look exactly alike. React Hook Form tracks the difference; this is how the
 * rest of the code asks it.
 *
 * Kept out of any one feature because the rule it supports - what gets sent -
 * is the same rule on every form, and a second copy of it would drift.
 */

/**
 * React Hook Form's report, narrowed to the names one form actually has.
 *
 * A plain field is `true` once it differs from what it was loaded with. An array
 * field is reported as an array of flags, so the presence of the key is what
 * matters rather than its contents.
 */
export type DirtyFieldMap<Name extends string> = Partial<
  Record<Name, boolean | boolean[]>
>;

/** True when the person has touched this field since the form was loaded. */
export function isFieldDirty<Name extends string>(
  dirtyFields: DirtyFieldMap<Name>,
  field: Name,
): boolean {
  const flag = dirtyFields[field];
  return Array.isArray(flag) ? true : Boolean(flag);
}
